import { useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";

export interface DocumentRenderTimingInput {
  workspaceId?: string;
  documentId?: string;
  blobId?: string;
  /** Length of the stored HTML, the dominant cost driver. */
  htmlChars: number;
  /** When the HTML query received its data (`dataUpdatedAt`, epoch ms). */
  htmlReceivedAt: number;
  /** The document is in the DOM. */
  isRendered: boolean;
}

/**
 * Reports how long opening a workspace document took, once per document.
 *
 * App Insights' fetch tracking stops at the response headers, so neither the
 * download of a large document nor the client-side parse and render showed up
 * anywhere (LAR-487). `msToFirstRender` runs from opening the page to the
 * document being in the DOM; `msHtmlToRender` is the part after the HTML was
 * available, i.e. the client-side cost alone.
 */
export function useDocumentRenderTiming({
  workspaceId,
  documentId,
  blobId,
  htmlChars,
  htmlReceivedAt,
  isRendered,
}: DocumentRenderTimingInput): void {
  const [openedAt] = useState(() => Date.now());
  const reportedBlobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isRendered || !blobId || reportedBlobIdRef.current === blobId) return;
    reportedBlobIdRef.current = blobId;

    const renderedAt = Date.now();
    const fromCache = htmlReceivedAt < openedAt;
    logger.info("Workspace document rendered", {
      operation: "workspaceDocumentRender",
      workspaceId,
      documentId,
      blobId,
      htmlChars,
      fromCache,
      msToFirstRender: renderedAt - openedAt,
      msHtmlToRender: renderedAt - Math.max(htmlReceivedAt, openedAt),
    });
  }, [
    isRendered,
    blobId,
    workspaceId,
    documentId,
    htmlChars,
    htmlReceivedAt,
    openedAt,
  ]);
}
